import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { ApiError } from "../../lib/api";
import { lessonsApi, type Lesson } from "../../lib/lessonsApi";
import { libraryApi, type LibraryEntry } from "../../lib/libraryApi";
import { Icon } from "../../components/ui/Icon";
import { PageHeader } from "../../components/ui/PageHeader";
import { useToast } from "../products/useToast";
import { shortDate } from "../products/text";
import { LessonSheet } from "./LessonSheet";
import { evidenceText, lessonStr, skillEntry, strengthWords } from "./lessonText";
import { useNative } from "../../../shared/phone/media";
import { LessonsNative } from "./LessonsNative";
import { LibrarySplit } from "./LibraryNative";
import { useCan } from "../../context/AuthContext";
import "../../styles/library.css";
import "../../styles/lessons.css";

const FILTERS: [string, string][] = [
  ["all", "All"],
  ["strong", "Strong"],
  ["medium", "Medium"],
  ["weak", "Weak"],
  ["disputed", "Disputed"],
];

const HOW: [string, string, string][] = [
  ["1 · WHERE", "Only from evidence", "A test your rules allowed it to call, a pattern across enough results, or something you told it."],
  ["2 · YOUR OK", "Proposed, then yours to accept", "New lessons arrive in the morning review. Only accepted ones shape plans and skills."],
  ["3 · HOW USED", "Guesses get re-tested", "Medium and weak lessons, or ones from another place, are re-tested before a plan leans on them."],
  [
    "4 · UPGRADES",
    "Better skills, with your approval",
    "When lessons point to a better way, the agent proposes a skill change. You approve it for one goal or your whole library.",
  ],
];

const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

interface Loaded {
  lessons: Lesson[];
  skills: LibraryEntry[];
}

/** What the agent has learned (`/library/lessons`): on a phone or an iPad the native screen beside the shelf, else the page. */
export function Lessons() {
  const native = useNative();
  return native ? <LibrarySplit sel="lessons" detail={<LessonsNative />} /> : <LessonsPage />;
}

/** The page on a computer: new lessons to accept or reject, the skill changes they suggest, and every accepted lesson (RV7). */
function LessonsPage() {
  const [data, setData] = useState<Loaded | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const may = useCan()("editor");
  // Why: an applied change is confirmed on the page where it was applied, as the prototype does, not on every visit after.
  const [applied, setApplied] = useState<string[]>([]);
  const { toast, toastNode } = useToast();

  const load = useCallback(async (): Promise<Loaded> => {
    const [l, lib] = await Promise.all([lessonsApi.list(), libraryApi.list()]);
    const next = { lessons: l.lessons, skills: lib.items.filter((e) => e.kind === "skill") };
    setData(next);
    return next;
  }, []);

  useEffect(() => {
    load().catch((e: unknown) => setErr(message(e)));
  }, [load]);

  /** Run one decision, then show the lessons as they are now. A decision made elsewhere first (409) reloads and says so. */
  const act = async (id: string, run: () => Promise<unknown>, done?: (fresh: Loaded) => string) => {
    setBusy(id);
    try {
      await run();
      const fresh = await load();
      if (done) toast(done(fresh));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) await load().catch(() => undefined);
      toast(message(e), false);
    } finally {
      setBusy(null);
    }
  };

  const lessons = data?.lessons ?? [];
  const skills = data?.skills ?? [];
  const waiting = lessons.filter((l) => l.state === "proposed");
  const changes = lessons.filter(
    (l) => l.state === "accepted" && l.skillChange && (l.skillChange.status === "proposed" || (l.skillChange.status === "applied" && applied.includes(l.id))),
  );
  const shown = lessons.filter((l) => l.state === "accepted" || l.state === "retired").toReversed();
  const list = shown.filter((l) => filter === "all" || lessonStr(l) === filter);
  const rejected = lessons.filter((l) => l.state === "rejected").length;
  const inSheet = lessons.find((l) => l.id === open) ?? null;
  const skillTitle = (name: string) => skillEntry(skills, name)?.latest.title ?? name;

  const applyChange = (l: Lesson, scope: "library" | "goal") =>
    act(
      l.id,
      async () => {
        await lessonsApi.applyChange(l.id, scope);
        setApplied((a) => [...a, l.id]);
      },
      (fresh) => {
        const title = skillEntry(fresh.skills, l.skillChange!.skill)?.latest.title ?? l.skillChange!.skill;
        if (scope === "goal") return `${title} is changed for that goal only.`;
        const sc = fresh.lessons.find((x) => x.id === l.id)?.skillChange;
        const made = fresh.skills.find((e) => e.latest.id === sc?.appliedTo);
        return made ? `${title} is now v${made.latest.version} in your library.` : `${title} is changed in your library.`;
      },
    );

  return (
    <section className="wrap ga lsn">
      <PageHeader title="What the agent has learned" back={{ to: "/library", label: "Library" }} sub="Each lesson shows its evidence and how sure it is. New ones wait for your OK." />
      {err && <p className="load-note">Couldn't load the lessons: {err}</p>}
      {!data && !err && <p className="load-note">Loading…</p>}
      {data && (
        <>
          <div className="lwait">
            {waiting.map((l) => (
              <section className="card wait" key={l.id} aria-label={`New lesson ${l.code}`}>
                <div className="need-top">
                  <span className="kind">
                    <Icon name="spark" />
                    New lesson · {shortDate(l.createdAt)}
                  </span>
                  <span className="str proposed">waiting for you</span>
                </div>
                <h4>{l.claim}</h4>
                {l.evidence.length > 0 && <div className="ev3">{evidenceText(l)}</div>}
                <p className="why">
                  <b style={{ textTransform: "capitalize" }}>{l.strength}:</b> {l.whyStrength ? `${l.whyStrength} ` : ""}
                  {l.applies ? `Where it applies: ${l.applies}.` : ""}
                </p>
                <div className="m-act">
                  {may && (
                    <>
                      <button type="button" className="btn sm" disabled={busy === l.id} onClick={() => act(l.id, () => lessonsApi.decide(l.id, "accept"), () => "Lesson accepted. It can now shape plans and skills.")}>
                        Accept
                      </button>
                      <button type="button" className="btn sm ghost" disabled={busy === l.id} onClick={() => act(l.id, () => lessonsApi.decide(l.id, "reject"), () => "Rejected. Kept in history, never used.")}>
                        Reject
                      </button>
                    </>
                  )}
                  <button type="button" className="chip link" onClick={() => setOpen(l.id)}>
                    Where it came from
                    <Icon name="right" />
                  </button>
                </div>
              </section>
            ))}
            {changes.map((l) => {
              const sc = l.skillChange!;
              const title = skillTitle(sc.skill);
              const entry = skillEntry(skills, sc.skill);
              if (sc.status === "applied") {
                const made = skills.find((e) => e.latest.id === sc.appliedTo);
                return (
                  <section className="card wait done" key={l.id} aria-label={`Skill change from ${l.code}`}>
                    <div className="clear">
                      <Icon name="check" />
                      {sc.appliedAs === "goal" ? (
                        <span>
                          <b>
                            {title} is changed for the goal “{l.learnedIn?.sentence ?? "it came from"}” only.
                          </b>{" "}
                          {entry ? `Your library keeps v${entry.latest.version}.` : "Your library keeps its version."}
                        </span>
                      ) : (
                        <span>
                          <b>
                            {title} is now {made ? `v${made.latest.version}` : "changed"} in your library.
                          </b>{" "}
                          New goals start with it. Running goals keep the version they have.{" "}
                          {sc.appliedTo && (
                            <Link className="chip link" to={`/library/${encodeURIComponent(sc.appliedTo)}`}>
                              View
                              <Icon name="right" />
                            </Link>
                          )}
                        </span>
                      )}
                    </div>
                  </section>
                );
              }
              return (
                <section className="card wait" key={l.id} aria-label={`Skill change from ${l.code}`}>
                  <div className="need-top">
                    <span className="kind">
                      <Icon name="spark" />
                      Suggested skill change
                    </span>
                    <span className="need-cost mono">from lesson {l.code}</span>
                  </div>
                  <h4>
                    {title}: {sc.change}
                  </h4>
                  {sc.canApply ? (
                    <div className="diff">
                      <div className="dl rm">{sc.before}</div>
                      <div className="dl add">{sc.after}</div>
                    </div>
                  ) : (
                    <div className="cnote">
                      <Icon name="info" />
                      <span>This change has no exact words to apply. Open the skill in the library and make the change there.</span>
                    </div>
                  )}
                  <p className="why">
                    <b>Why:</b> {l.code} is {strengthWords(l)}
                    {l.whyStrength ? `: ${l.whyStrength}` : "."}
                  </p>
                  <div className="m-act">
                    {sc.canApply && may ? (
                      <>
                        <button type="button" className="btn sm" disabled={busy === l.id} onClick={() => applyChange(l, "library")}>
                          Approve for my library
                        </button>
                        {l.learnedIn && (
                          <button type="button" className="btn sm ghost" disabled={busy === l.id} onClick={() => applyChange(l, "goal")}>
                            This goal only
                          </button>
                        )}
                      </>
                    ) : (
                      entry && (
                        <Link className="btn sm" to={`/library/${encodeURIComponent(entry.latest.id)}`}>
                          Open {title}
                        </Link>
                      )
                    )}
                    {may && (
                      <button type="button" className="skip" disabled={busy === l.id} onClick={() => act(l.id, () => lessonsApi.declineChange(l.id), () => "Skill change declined. The lesson stays.")}>
                        Not now
                      </button>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
          <div className="fchips" role="group" aria-label="Filter by how sure">
            {FILTERS.map(([k, label]) => (
              <button type="button" key={k} className={filter === k ? "on" : ""} aria-pressed={filter === k} onClick={() => setFilter(k)}>
                {label}
                <span>{k === "all" ? shown.length : shown.filter((x) => lessonStr(x) === k).length}</span>
              </button>
            ))}
          </div>
          <div className="lgrid">
            {list.map((l) => (
              <button type="button" className="lcard" key={l.id} onClick={() => setOpen(l.id)}>
                <div className="lc-h">
                  <span className="lid">{l.code}</span>
                  <span className={`str ${lessonStr(l)}`}>{lessonStr(l)}</span>
                </div>
                <b>{l.claim}</b>
                <p>{evidenceText(l)}</p>
                <div className="st-meta">
                  {l.applies && <span className="chip">{l.applies}</span>}
                  {l.usedBy.length > 0 && <span className="chip auto">Used by {l.usedBy.length}</span>}
                </div>
              </button>
            ))}
            {list.length === 0 && (
              <div className="card clear">
                <Icon name="search" />
                <span>No lessons here.</span>
              </div>
            )}
          </div>
          {rejected > 0 && (
            <p className="fine lsn-rej">
              {rejected} rejected {rejected === 1 ? "lesson" : "lessons"} kept in history, never used.
            </p>
          )}
          <details className="card howx">
            <summary>
              How lessons work
              <Icon name="chev" />
            </summary>
            <div className="howl">
              {HOW.map(([n, b, p]) => (
                <div key={n}>
                  <span className="n">{n}</span>
                  <b>{b}</b>
                  <p>{p}</p>
                </div>
              ))}
            </div>
          </details>
        </>
      )}
      <LessonSheet lesson={inSheet} busy={busy !== null && busy === open} onClose={() => setOpen(null)} onCall={may ? (l, call) => act(l.id, () => lessonsApi.call(l.id, call)) : undefined} />
      {toastNode}
    </section>
  );
}
